// NestPlot user documentation — rendered at /docs and /docs/:slug by
// ../DocsPage.tsx.
//
// One place, plain data. Each body line renders as:
//   "### " → sub-heading, "- " → bullet, "> " → callout,
//   anything else → paragraph. Inline **bold** and `code` are supported.
//
// This is CUSTOMER-FACING help content — how an operator uses NestPlot.
// Keep it in sync when features ship. Brand voice: "we" / "NestPlot" /
// "Contact sales"; never name a person. NestPlot serves four verticals —
// self-storage, multifamily, parking, and RV/boat — so prefer
// vertical-agnostic wording and call out per-vertical differences
// explicitly.

export interface DocTopic {
  slug: string;
  title: string;
  icon: string;
  summary: string;
  body: string[];
}

export const DOCS_META = {
  title: "NestPlot documentation",
  description:
    "How every part of NestPlot works — setting up your property, taking rentals online, collecting rent, gate access, leases, your team, and more.",
};

export const DOC_TOPICS: DocTopic[] = [
  {
    slug: "getting-started",
    title: "Getting started",
    icon: "🚀",
    summary: "From signup to your first unit online in an afternoon.",
    body: [
      "**Create your account.** Sign up at nestplot.com/signup — you get a free trial, no card required. Confirm your email, and your first visit lands on setup. After that, **Today** is your home screen.",
      "### Pick your vertical",
      "NestPlot runs **self-storage, multifamily, parking, and RV/boat** operations from one app. You choose your property type when you create your first facility — that decides what the app and your public listing call things (units vs. apartments vs. spots vs. slips) and which flow renters go through.",
      "### The three things to set up first",
      "- **Add your property** — create a facility and add your units (or import them). See *Add your property*.",
      "- **Get paid** — connect your bank through Stripe so rent settles straight to you. See *Collecting rent*.",
      "- **Go live** — your public rental page is ready the moment you have units; share the link or point your domain at it. See *Listings & online rentals*.",
      "### What we send you after signup",
      "About six hours after you confirm your email we send one welcome note pointing you at the parts of NestPlot most likely to matter for your vertical, with a link to book a walkthrough. If you set up a unit or a tenant, we know you're off and running and leave you alone. Everything we send lands during daytime in your own timezone — nothing should arrive at 3am.",
      "### Getting around",
      "The left menu groups everything: **Today** (your dashboard), **Units**, **Tenants**, **Leases**, **Billing**, **Gate**, **Reports**, plus **Settings** and **Team**. Multifamily operators also get **Leasing** and **Tours**.",
      "> Only a card and no time? Book a walkthrough at nestplot.com/book-demo — we'll set it up with you in 20 minutes.",
    ],
  },
  {
    slug: "add-your-property",
    title: "Add your property",
    icon: "🏢",
    summary: "Facilities, units, floor plans, and the fields that matter per vertical.",
    body: [
      "### Create a facility",
      "A **facility** is one physical property — a storage site, an apartment building, a parking lot, a marina. Add its name, address, timezone, and phone. You can run as many facilities as your plan allows, each with its own units, branding, and gate.",
      "### Add units",
      "A **unit** is one rentable space. Add them by hand, bulk-add with AI (describe your inventory in plain English on **Units → AI fill** and we draft the roster), or import from your old system (see *Migrating your data*). Each unit has a number, a monthly rate, and a state (vacant / reserved / rented).",
      "What else a unit carries depends on your vertical:",
      "- **Self-storage** — size (10×10), climate control, drive-up vs. interior access.",
      "- **Multifamily** — beds, baths, square footage, floor, amenities, pet policy, utilities, available date.",
      "- **Parking** — coverage (garage / covered / open), height clearance, EV charging, in/out privileges.",
      "- **RV & boat** — length, height, coverage, hookups, wash-bay, dry-stack limits.",
      "### Floor plans & unit types",
      "Group interchangeable units under a **floor plan** (multifamily) or a size/type catalog (storage, parking, marina). Renters pick the plan or size; you assign the specific unit. A plan carries its own photos, rent, and features so your listing reads the way renters think.",
      "> Vacant units show on your public page automatically. Set a unit's state to reserved or rented (or just move a tenant in) and it drops off the listing.",
    ],
  },
  {
    slug: "listings-and-online-rentals",
    title: "Listings & online rentals",
    icon: "🌐",
    summary: "Your branded public page, and how renters book or apply.",
    body: [
      "### Your public rental page",
      "Every facility has a branded public listing the moment it has vacant units. It shows your logo, colors, photos, available units with prices, and the right call-to-action for your vertical. Share the link, add it to your site, or point a custom domain at it (**Settings → Custom domain**).",
      "### Two renter journeys — by vertical",
      "NestPlot runs the journey your vertical expects. Don't mix them:",
      "- **Storage, parking, RV/boat → instant rent.** The renter picks a unit, enters their details, signs a lease in-flow, pays by card, and gets a gate code immediately. No phone tag. This is the whole point — renters self-serve, you wake up to a rented unit.",
      "- **Multifamily → apply, then screen.** The prospect tours and submits an application; you run credit / criminal / income screening and then sign a lease. See *Multifamily leasing*.",
      "### Photos & media",
      "Add facility photos and per-unit or per-floor-plan photos under the facility's media panel. For wayfinding, upload a facility-layout image (\"Building A · Floor 2\", \"Dock C\") so renters can find a specific space.",
      "> Want online rentals but prefer to approve each one? You can review reservations before a gate code is issued — ask us to turn on reservation-approval for a facility.",
    ],
  },
  {
    slug: "move-ins-and-leases",
    title: "Move-ins & leases",
    icon: "📝",
    summary: "The move-in wizard, state-aware lease templates, and e-signature.",
    body: [
      "### Moving a tenant in",
      "Renters move themselves in from your public page. To do it yourself, use **Move-in → New**: pick the unit, enter the tenant, set the rate and move-in date, and NestPlot creates the tenant, the lease, and (for self-serve-gate facilities) a gate code.",
      "### Leases that match your state",
      "NestPlot generates a rental agreement from a template tuned to your state's law — grace periods, late-fee caps, lien timelines, and required notices are built in. You can customize your template under **Leases → Templates**; changes version cleanly so signed leases stay intact.",
      "### E-signature",
      "Leases are sent for e-signature automatically. The tenant gets an email (and a button in their portal) to review and sign; when they do, the signed PDF is stored and the lease is marked signed. If a tenant hasn't signed, we send gentle reminders at 1, 4, and 7 days and keep you in the loop — you don't have to chase them.",
      "> Your signed leases live under each tenant and are exportable any time — your data is always yours.",
    ],
  },
  {
    slug: "collecting-rent",
    title: "Collecting rent",
    icon: "💳",
    summary: "Connect your bank, autopay, recording payments, and dunning.",
    body: [
      "### Rent settles to you — we take 0% of it",
      "Connect your bank through Stripe (**Billing → Connect**). Rent settles **directly into your account** — NestPlot never takes a cut. You pay only Stripe's processing fee, plus your flat monthly software fee and nothing else on rent.",
      "### Pay by bank (ACH) or card",
      "Tenants can pay by **bank account (ACH)** or card from their portal — **bank is the recommended default because it's far cheaper**. ACH runs at **0.8%, capped at $5.00** per payment; cards run at **2.9% + $0.30**. On $1,800 rent that's about **$5 by bank vs. ~$52 by card** — money that stays with you. Bank verification is instant (the tenant signs into their bank through Stripe); card stays available for anyone who prefers it.",
      "> **Timing:** a card payment posts immediately; an ACH (bank) payment settles in **1–2 business days**, so the balance clears a couple days after the tenant pays. Autopay can run on a saved bank account or card.",
      "### Autopay & payment method on file",
      "Tenants save a bank account or card in their portal and are on autopay by default, so rent collects itself each month. You can also record a cash or check payment under **Billing** for anyone who pays off-platform.",
      "### Posting rent & past-due",
      "On each tenant's monthly anniversary we post their rent to their balance. After your state's grace period, an unpaid balance marks the tenant past-due and starts the reminder ladder.",
      "### Dunning that never locks out a paying tenant",
      "Past-due tenants get an automated reminder sequence — reminder, second notice, then the pre-lien notice if it comes to that — by text or email, on the timeline your state allows. We also retry failed autopay payments (card or bank) automatically.",
      "> **Your gate stays open.** Dunning never revokes a gate code. The only thing that turns a code off is you, explicitly. A paying — or even a late — tenant always gets to their stuff until you decide otherwise.",
    ],
  },
  {
    slug: "gate-and-access",
    title: "Gate & building access",
    icon: "🔑",
    summary: "Connect your gate or smart locks, issue access, and how it's protected.",
    body: [
      "### Access fits your property type",
      "How tenants get in depends on what you run, and the settings adapt to it:",
      "- **Self-storage, parking, RV/boat, marina** — a **keypad gate**. Connect PTI Falcon, SecurGuard, Insomniac (OpenTech), Noke, DaVinciLock, IntelliGate, or Janus under **Settings → Gate**.",
      "- **Apartments / multifamily** — **smart locks & intercoms via Seam**. One connection covers Yale, Schlage, SALTO, August, Brivo, Kisi, ButterflyMX, Latch, igloohome and more. (Keypad-gate vendors are hidden here — they're a storage thing, not an apartment-building thing.)",
      "- **Any property** — if you don't have a connected system, add plain **building-access instructions** (callbox number, key pickup, smart-lock app steps). Your tenant sees them in their portal and welcome email.",
      "### Connect a keypad gate",
      "Under **Settings → Gate**, pick your provider and enter that facility's credentials, then **Save**. We validate it on the spot — a bad URL or half-filled credentials are rejected with a clear message instead of looking saved. Use **Test connection** any time to confirm it's reaching the gate.",
      "### Connect smart locks / intercom (Seam)",
      "In the **Building access** section, paste your Seam workspace API key and the device/entry ID for the building, then **Save** — we test the connection immediately. Seam ([seam.co](https://seam.co)) links your existing locks and intercoms; each tenant's access code is programmed onto them automatically.",
      "### Codes issue themselves",
      "When a tenant moves in (online or by you), an access code is generated and pushed to your gate or lock automatically. You can rotate, re-sync, or revoke a code on the **Gate** page.",
      "### We tell you if it isn't actually working",
      "A connection that saves but can't reach your hardware is the worst kind of surprise — so we verify it. If a saved gate fails its test (or stops responding later), you'll see a banner in the app and we'll flag it to our team, before a tenant is ever stranded at the gate.",
      "> **We never auto-revoke a tenant's access.** Being past due never pulls a tenant's code — the only thing that turns access off is an explicit revoke by you. Your tenants keep getting in until you decide otherwise.",
    ],
  },
  {
    slug: "tenant-portal",
    title: "The tenant portal",
    icon: "👤",
    summary: "What your tenants can do, and how they sign in.",
    body: [
      "### Passwordless sign-in",
      "Tenants sign in to their portal with a one-tap magic link — they enter their email or phone and we text or email them a secure sign-in link that expires shortly. No passwords to forget or reset.",
      "### What tenants can do",
      "- See their unit, balance, and lease, and download the signed PDF.",
      "- Pay rent by bank (ACH) or card, save a payment method, and turn autopay on or off. Bank is the default — it costs them nothing extra and saves you the card fee.",
      "- Get in: their gate/lock access code (storage, parking, RV, marina) or a \"How to get in\" card with your building-access instructions (apartments).",
      "- Request a move-out, or cancel a reservation within the cancellation window.",
      "- Export their own payment history — their data is theirs.",
      "> The portal is branded to the facility — your logo, your colors — and tenant replies to NestPlot emails can route straight to you.",
    ],
  },
  {
    slug: "multifamily-leasing",
    title: "Multifamily leasing",
    icon: "🏬",
    summary: "Tours, applications, and resident screening — the apply-then-screen flow.",
    body: [
      "### A different journey",
      "Apartments don't rent instantly — prospects apply and you screen them. On a multifamily facility your public page shows **Apply** (and **Request a tour**), not an instant-rent button.",
      "### Tours",
      "Prospects request a tour from your listing; manage requests under **Tours**. Offer in-person, self-guided, or virtual tours, and set your showing window so requests land in hours that work for you.",
      "### Applications",
      "An application lands under **Leasing**. Review the applicant, then run screening (credit, criminal, eviction, income) in-flow. You decide who pays the screening fee — you, or the applicant via a payment link before the check runs.",
      "### From approved to leased",
      "Approve an application, generate the lease from your state template, send it for e-signature, and the resident moves in — same leases, same portal, same rent collection as every other vertical.",
      "> Screening runs through Checkr. We never store SSNs or dates of birth; applicants enter them directly into the check.",
    ],
  },
  {
    slug: "team-and-permissions",
    title: "Team & permissions",
    icon: "👥",
    summary: "Invite your team, set roles, and scope people to facilities.",
    body: [
      "### Invite your team",
      "Add teammates under **Team → Invite**. Each person gets a role: **owner** (you), **admin**, **manager**, or **viewer**. They accept by email and set their own password — or sign in with Google.",
      "### What each role can do",
      "- **Owner** — everything, including billing and cancelling the subscription.",
      "- **Admin** — everything except cancelling the subscription.",
      "- **Manager** — day-to-day operations; sensible defaults you can fine-tune.",
      "- **Viewer** — read-only.",
      "### Fine-grained control",
      "Beyond roles, you can toggle individual permissions per teammate (for example, cap how large a refund a manager can issue) and **scope a person to specific facilities** so a site manager only sees their own site.",
      "> Someone from your company already have an account? When they sign in with your work domain, they can request to join your workspace and you approve them from **Team**.",
    ],
  },
  {
    slug: "migrating-your-data",
    title: "Migrating your data",
    icon: "📦",
    summary: "Bring your tenants over from storEDGE, SiteLink, AppFolio, and more.",
    body: [
      "### Import in minutes, not days",
      "Export a tenant list from your current system as a spreadsheet and upload it under **Tenants → Import**. We auto-detect the columns — a raw **storEDGE**, **SiteLink**, **AppFolio**, **Yardi**, **Buildium**, or **DoorLoop** export imports cleanly with no manual mapping.",
      "### What comes over",
      "Your tenants, their units (created automatically if they don't exist yet), rates, balances, move-in dates, gate codes, and autopay flags. We match on email so re-running an import never creates duplicates.",
      "### Preview before you commit",
      "Every import shows a preview first: the detected column mapping and any rows that need attention (a missing name, unit, or rate). Fix those in your sheet and re-upload — valid rows import, flagged rows are reported.",
      "### Required columns",
      "At minimum each row needs a **name**, a **unit number**, and a **monthly rate**. Everything else — email, phone, size, balance, move-in, gate code, autopay, notes — is optional and filled when present.",
      "> Want a hand with a tricky export? Send it to us and we'll map it for you.",
    ],
  },
  {
    slug: "reports-and-pricing",
    title: "Reports & pricing",
    icon: "📊",
    summary: "Occupancy, revenue, delinquency, and dynamic pricing suggestions.",
    body: [
      "### Your numbers",
      "**Reports** gives you occupancy, revenue over time, and delinquency at a glance — per facility, or rolled up across your whole portfolio on the **Portfolio** dashboard.",
      "### Dynamic pricing (advisory)",
      "NestPlot watches your occupancy and suggests rate changes per unit size. Suggestions are **advisory** — nothing changes your rates without you. If you want, you can opt a facility into bounded auto-apply (only small changes, up to a cap you set).",
      "> Exports are a click away: tenants, payments, and units each export to CSV from the operator app.",
    ],
  },
  {
    slug: "notifications-and-reminders",
    title: "Notifications & reminders",
    icon: "🔔",
    summary: "The automatic messages NestPlot sends your tenants and you.",
    body: [
      "### Working for you in the background",
      "NestPlot sends the right message at the right time so you don't have to:",
      "- **Move-in & lease** — welcome, lease-to-sign, and sign reminders at 1, 4, and 7 days for anyone who hasn't signed yet (you're copied).",
      "- **Rent** — receipts, past-due reminders on your state's timeline, and failed-card retries.",
      "- **Gate** — the tenant gets their code when it's issued.",
      "### You stay in control",
      "Tune which emails go out under **Settings → Notifications**. Tenants who reply STOP to a text are opted out automatically (and we honor it).",
      "> Every message is branded as your facility where it makes sense, and tenant replies can route straight to your inbox.",
    ],
  },
  {
    slug: "settings-and-branding",
    title: "Settings & branding",
    icon: "🎨",
    summary: "Make the tenant experience look like you — logo, colors, domain.",
    body: [
      "### Brand it",
      "Upload your logo and set your brand colors under **Settings → Branding**. They paint your public rental page, the tenant portal, and the emails NestPlot sends on your behalf.",
      "### Your own domain",
      "Point a custom domain (or subdomain) at your facility's rental page so renters stay on your brand end-to-end. We handle the certificate.",
      "### Email from you",
      "Set the from-name and reply-to for a facility so tenant emails look like they came from your office and replies land where you want them. For higher volume, connect your own Gmail or Zoho mailbox.",
    ],
  },
  {
    slug: "api-and-integrations",
    title: "API & integrations",
    icon: "🔌",
    summary: "Read-only API keys, accounting sync, and what connects.",
    body: [
      "### API keys",
      "Generate a read-only API key under **Settings → API keys** to pull your facilities, units, tenants, payments, and a portfolio summary into your own tools. Keys are scoped (you choose what each can read) and can be limited to specific facilities. The full key is shown once — store it safely.",
      "### Accounting",
      "Sync receipts and payments to **QuickBooks Online**, **Xero**, or **Sage Intacct** per legal entity, so your books stay current without double entry.",
      "### What else connects",
      "Gate providers (seven of them), card payments and payouts via Stripe, e-signature via DocuSeal, resident screening via Checkr, certified mail for lien notices, and SMS — all built in, nothing for you to wire up beyond entering your own credentials where a facility needs them.",
      "> Need a write API or a specific integration? Contact sales — tell us what you're building.",
    ],
  },
  {
    slug: "api-and-webhooks",
    title: "API & webhooks",
    icon: "🪝",
    summary: "Pull your own data, and get pushed an event when something happens.",
    body: [
      "Read your own data with your own tools, and get notified the moment something changes. Everything here is set up at **Settings → API keys** and **Settings → Webhooks**.",
      "### Calling the API",
      "Create a scoped, read-only key under **Settings → API keys** — it's shown once, so store it safely. Send it as a bearer token against `https://api.nestplot.com/v1`:",
      "```\ncurl https://api.nestplot.com/v1/facilities \\\n  -H \"Authorization: Bearer npk_live_...\"\n```",
      "Endpoints: `GET /v1/facilities`, `GET /v1/facilities/{id}/units`, `GET /v1/facilities/{id}/tenants`, and `GET /v1/portfolio/summary`. A key only ever carries the scopes you grant it, and can be limited to specific facilities. Revoking a key takes effect on the next request.",
      "### Webhooks",
      "Add an https endpoint under **Settings → Webhooks** and we POST JSON to it when something happens. Events: `tenant.created`, `lease.signed`, `payment.succeeded`, `payment.failed`, and `application.received`. Leave the event list empty to receive all of them **except `audit.recorded`**, which you must tick explicitly — it's a high-volume security feed (every audit entry, with sign-in IPs) meant for a SIEM, so it never arrives at an endpoint that didn't ask for it.",
      "The body is `{ \"event\", \"created_at\", \"data\" }`. Reply with any 2xx; anything else (or a timeout past 10 seconds) is a failure, and we retry after 1 minute, 5, 30, 2 hours and 6 hours before giving up on that delivery — about nine hours, so an afternoon of downtime at your end loses nothing. An endpoint that fails 25 deliveries in a row is switched off and tells you why on the page; fix it and re-enable it. **Send test** delivers a sample payload and shows you the exact response.",
      "### Verifying the signature",
      "Every request carries `X-NestPlot-Signature: t=<unix seconds>,v1=<hex>`, where the hex is HMAC-SHA256 of `\"<t>.<raw body>\"` using that endpoint's signing secret. Sign the **raw** body, before any JSON parsing. Reject anything where `t` is more than a few minutes old — the timestamp is inside the signed material so a captured request can't be replayed later. Compare with a constant-time function, not `==`.",
      "```\nimport hmac, hashlib\nts, sig = [p.split(\"=\", 1)[1] for p in header.split(\",\")]\nexpected = hmac.new(secret.encode(), f\"{ts}.{body}\".encode(), hashlib.sha256).hexdigest()\nassert hmac.compare_digest(expected, sig)\n```",
      "### If your endpoint needs to authenticate us",
      "Our signature proves a payload came from us and wasn't altered — it does not sign us IN, and plenty of receivers (Zapier, n8n, most internal services) reject an unauthenticated POST. Each endpoint takes one optional `Authorization` header (for example `Bearer <your token>`) that we send on every delivery. It's stored encrypted and never shown again — the page lists that a header is set, not its value.",
      "> The public API is read-only today. If you need to push data in — create a tenant, record a payment — tell us what you're building.",
    ],
  },
  {
    slug: "billing-and-your-plan",
    title: "Billing & your plan",
    icon: "🧾",
    summary: "Plans, your free trial, and managing your subscription.",
    body: [
      "### Simple, flat pricing",
      "NestPlot is a flat monthly software fee — **Starter**, **Growth**, and **Portfolio** plans scale with how much you run, and **Enterprise** is quoted for large portfolios. We take **0% of your tenant rent** on every plan. Full pricing is at nestplot.com/pricing.",
      "### Your trial",
      "Every account starts with a free trial — explore the whole product, no card required. Add a plan from **Billing** whenever you're ready; your trial won't interrupt tenant rent or gate access.",
      "### Managing your subscription",
      "Open the billing portal from **Billing** to change plan, update your card, or download invoices. If a subscription payment lapses, your tenant flows keep running — rent still collects, dunning still runs, gate codes stay live — only your ability to change settings pauses until you're current.",
      "> Your subscription covers the software; third-party costs like background checks or certified mail are always billed at cost.",
    ],
  },
  {
    slug: "getting-help",
    title: "Getting help",
    icon: "💬",
    summary: "How to reach us and how fast we respond.",
    body: [
      "### Talk to us",
      "Email **info@nestplot.com** or use the contact form at nestplot.com/contact. We respond within one hour, Monday–Saturday, 9–6 ET.",
      "### See it live",
      "Book a 20-minute walkthrough at nestplot.com/book-demo — we'll set up your first facility with you and answer anything.",
      "### Moving from another system?",
      "Send us your export and we'll help you migrate. Most operators are live the same day.",
    ],
  },
];

export function findTopic(slug: string): DocTopic | undefined {
  return DOC_TOPICS.find((t) => t.slug === slug);
}
